<template>
  <div class="policy-page guide-page">
    <header class="policy-header">
      <router-link class="brand" to="/">
        <span class="brand-mark" aria-hidden="true"><span></span></span>
        <span>
          <strong>Codex API</strong>
          <em>codex API 中转服务</em>
        </span>
      </router-link>
      <div class="policy-actions">
        <router-link to="/">返回首页</router-link>
        <router-link to="/login">登录</router-link>
      </div>
    </header>

    <main class="policy-shell guide-shell">
      <section class="policy-card guide-card">
        <p class="policy-kicker">CODEX API 新手教程</p>
        <h1>从注册到第一次 Codex 调用</h1>
        <p class="policy-summary">
          用本站创建密钥，再按自己的工作方式接入 Codex 桌面端、Codex CLI 或 CCSwitch。完成后，调用额度与使用记录会回到本站控制台统一查看。
        </p>

        <div class="guide-links" aria-label="常用入口">
          <a href="/register">注册账户</a>
          <a href="/login">登录控制台</a>
          <a href="/dashboard/keys">API 密钥</a>
          <a href="/dashboard/usage">使用记录</a>
          <a href="https://developers.openai.com/codex/" target="_blank" rel="noopener">Codex 官方文档</a>
        </div>

        <div class="guide-methods" aria-label="安装方式">
          <div class="guide-method guide-method-active">
            <span>方式一</span>
            <strong>命令行版（CLI）</strong>
            <p>推荐先使用这一种。本页已提供从安装、配置到验证的完整步骤。</p>
          </div>
          <div class="guide-method">
            <span>方式二</span>
            <strong>桌面端</strong>
            <p>桌面端安装步骤将在提供对应参考资料后补充，不影响使用命令行版。</p>
          </div>
        </div>

        <div class="guide-overview" aria-label="新手路径">
          <div v-for="item in overview" :key="item.step" class="guide-overview-item">
            <span>{{ item.step }}</span>
            <strong>{{ item.title }}</strong>
            <p>{{ item.text }}</p>
          </div>
        </div>

        <div class="guide-list">
          <article v-for="section in orderedSections" :key="section.step" class="guide-section">
            <div class="guide-section-head">
              <span>{{ section.step }}</span>
              <div>
                <h2>{{ section.title }}</h2>
                <p>{{ section.summary }}</p>
              </div>
            </div>

            <div v-if="section.resources" class="guide-resources">
              <a
                v-for="resource in section.resources"
                :key="resource.href"
                :href="resource.href"
                target="_blank"
                rel="noopener"
              >{{ resource.label }}</a>
            </div>

            <div v-if="section.beginnerTip" class="guide-beginner-tip">
              <strong>{{ section.beginnerTip.title }}</strong>
              <p>{{ section.beginnerTip.text }}</p>
            </div>

            <ol>
              <li v-for="item in section.items" :key="item.title">
                <strong>{{ item.title }}</strong>
                <p>{{ item.text }}</p>
              </li>
            </ol>

            <figure v-if="section.screenshot" class="guide-screenshot">
              <img :src="section.screenshot.src" :alt="section.screenshot.alt">
              <figcaption>{{ section.screenshot.caption }}</figcaption>
            </figure>

            <div v-if="section.commands" class="guide-commands">
              <div v-for="command in section.commands" :key="command.label" class="guide-command">
                <div class="guide-command-head">
                  <span>{{ command.label }}</span>
                  <el-button
                    size="mini"
                    plain
                    icon="el-icon-document-copy"
                    @click="copyCommand(command.value)"
                  >复制</el-button>
                </div>
                <code>{{ command.value }}</code>
                <p v-if="command.hint" class="guide-command-hint">{{ command.hint }}</p>
              </div>
            </div>
          </article>
        </div>

        <div class="guide-note">
          <strong>第一次使用时的建议</strong>
          <p>先只保留一个启用的密钥完成验证。确认 Codex 能正常返回结果后，再为不同设备、项目或成员创建独立密钥，便于在本站查看用量和定位问题。</p>
        </div>

        <div class="guide-actions">
          <el-button round @click="$router.push('/login')">登录并创建密钥</el-button>
          <el-button type="primary" round @click="$router.push('/register')">创建账户</el-button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'GettingStartedView',
  data() {
    return {
      overview: [
        { step: '01', title: '准备本站账户', text: '注册、登录并确认有可用套餐。' },
        { step: '02', title: '安装 CLI', text: '安装 Node.js 和 Codex 命令行工具。' },
        { step: '03', title: '写入本站配置', text: '用 API 密钥生成并执行配置脚本。' },
        { step: '04', title: '启动并验证', text: '完成首次调用，再查看使用记录。' }
      ],
      sections: [
        {
          step: '01',
          order: 1,
          title: '先完成本站账户与套餐准备',
          summary: '密钥与套餐都在 Codex API 控制台中管理。',
          items: [
            { title: '注册并登录', text: '打开“注册账户”，使用可接收验证码的邮箱完成注册；已有账户可直接打开“登录控制台”。' },
            { title: '确认可用额度', text: '登录后依次进入“我的套餐”查看套餐；如持有兑换码，打开“兑换”输入兑换码后领取余额或日卡。' },
            { title: '创建 API 密钥', text: '打开“API 密钥”，点击“创建密钥”，填写易识别的设备或项目名称，再选择需要使用的余额或日卡分组。' }
          ],
          screenshot: {
            src: '/guide/step-login-form.png',
            alt: 'Codex API 登录控制台截图',
            caption: '截图 1：在登录控制台输入邮箱、密码和验证码后进入用户控制台。'
          }
        },
        {
          step: '05',
          order: 5,
          title: '方式二：Codex 桌面端（等待补充）',
          summary: '桌面端需要按照对应版本的安装流程操作；本教程先完成命令行版，避免把两种方式混在一起。',
          items: [
            { title: '暂时先完成命令行版', text: '如果你还没有使用过 Codex，请先按上方“方式一：命令行版（CLI）”完成安装和验证。桌面端参考资料到位后，会在这里补上同样细致的图文步骤。' }
          ]
        },
        {
          step: '02',
          order: 2,
          title: '方式一：命令行版（CLI）- 安装环境',
          summary: 'Codex CLI 依赖 Node.js 和 npm。Node.js 版本必须不低于 18，建议使用 22 或更高的 LTS 稳定版。',
          resources: [
            { label: 'Node.js LTS 下载地址：https://nodejs.org/en/download', href: 'https://nodejs.org/en/download' },
            { label: 'Codex CLI 官方文档：https://developers.openai.com/codex/cli/', href: 'https://developers.openai.com/codex/cli/' }
          ],
          beginnerTip: {
            title: '开始前只要确认一件事',
            text: 'Windows 用户全程使用 PowerShell；Mac 用户全程使用 Terminal。不要同时安装多个 Node.js 版本，也不需要安装 Docker。每完成一步，确认看到“成功标志”后再做下一步。'
          },
          items: [
            { title: '下载正确的 Node.js 安装包', text: '点击上方“Node.js LTS 下载地址”。Windows：向下找到并点击“Windows Installer (.msi)”。Mac：在系统选项中选择 macOS，再点击“.pkg”安装包。只下载标有 LTS 的稳定版，不要选择 Docker、.zip 或源码包。' },
            { title: '像普通软件一样完成安装', text: '双击刚下载的文件。Windows 连续点击 Next，勾选同意协议后点击 Install；Mac 双击 .pkg 后按“继续”完成安装。除非安装程序提示，否则不要修改安装位置。完成后关闭安装窗口。' },
            { title: '打开输入命令的窗口', text: 'Windows：按键盘 Windows 键，输入 PowerShell，按 Enter。Mac：按 Command + 空格，输入 Terminal，按 Enter。会出现一个可以输入文字的黑色或深色窗口，这就是终端。' },
            { title: '先检查 Node.js 是否安装成功', text: '在终端里逐条复制下方两条检查命令，粘贴后按 Enter。Windows 可在 PowerShell 中右键粘贴；Mac 用 Command + V。每条命令会返回以 v 或数字开头的版本号，这就表示成功。若 node 版本低于 18，请重新安装当前 LTS 版本。' },
            { title: '安装 Codex CLI', text: '先复制“常规安装”命令，粘贴到同一个终端并按 Enter。等待命令执行结束，期间不要关闭窗口。若下载长时间没有进度或网络超时，再使用“网络慢时备用安装”命令；两条安装命令只执行其中一条。' },
            { title: '第一次启动并登录', text: '复制最后一条 codex 命令并按 Enter。浏览器会打开登录页面，使用自己的 OpenAI 账号完成登录；随后回到终端，看到 Codex 的欢迎或输入界面即表示完成。' }
          ],
          screenshot: {
            src: '/guide/step-node-installer.png',
            alt: 'Node.js Windows 安装包下载位置',
            caption: '截图 2：Windows 用户在 Node.js 下载页选择 Windows Installer (.msi)。右侧的 .zip 不需要下载。Mac 用户在上方系统选项切换到 macOS 后选择 .pkg。'
          },
          commands: [
            { label: '第 1 条：检查 Node.js', value: 'node --version', hint: '成功标志：显示类似 v24.18.1 的版本号。' },
            { label: '第 2 条：检查 npm', value: 'npm --version', hint: '成功标志：显示一串数字，例如 11.16.0。' },
            { label: '第 3 条：常规安装 Codex CLI', value: 'npm install -g @openai/codex', hint: '优先执行这一条。等待执行结束，出现 added、changed 或没有红色报错都表示安装已完成。' },
            { label: '网络慢时备用：使用 npm 镜像安装', value: 'npm install -g @openai/codex --registry=https://registry.npmmirror.com', hint: '只有常规安装下载慢、超时或失败时才执行。不要和上面的常规安装重复执行。' },
            { label: '第 4 条：确认 Codex CLI 已安装', value: 'codex --version', hint: '成功标志：显示 Codex 的版本号。' },
            { label: '第 5 条：启动 Codex 并登录', value: 'codex', hint: '浏览器打开后完成登录，再返回此终端。' }
          ]
        },
        {
          step: '03',
          order: 3,
          title: '方式一：命令行版（CLI）- 写入本站配置',
          summary: '安装完成后，把本站生成的配置写入 Codex，后续调用将通过当前 API 密钥计费和记录。',
          items: [
            { title: '下载本站配置脚本', text: '登录本站后打开“API 密钥”，选择刚创建的密钥并点击“一键配置 Codex”，客户端选择 Codex CLI。Windows 下载文件名以 -codex-install.bat 结尾；Mac 下载文件名以 -codex-install.sh 结尾。' },
            { title: '运行下载的配置脚本', text: 'Windows 直接双击下载的 .bat 文件；Mac 打开 Terminal 后执行下方 Mac 命令。看到“配置完成”后关闭当前终端并重新打开一个新的终端。脚本只写入 .codex 文件夹，并会自动备份旧配置。' },
            { title: '进入你的项目并启动', text: '把下方路径中的“你的项目文件夹”换成实际项目路径。粘贴第一条或第二条命令后按 Enter，再粘贴 codex 启动命令。' }
          ],
          commands: [
            { label: 'Windows：直接双击下载的配置文件', value: '你的密钥名-codex-install.bat', hint: '在“下载”文件夹找到这个 .bat 文件并双击。文件名会随你创建的密钥名称变化。' },
            { label: 'Mac：在 Terminal 中运行下载的配置文件', value: 'cd ~/Downloads\nsh "./你的密钥名-codex-install.sh"', hint: '将“你的密钥名”替换为实际下载文件名；Mac 的默认下载目录是 Downloads。' },
            { label: 'Windows：进入项目文件夹', value: 'cd "D:\\你的项目文件夹"', hint: '例如：cd "D:\\work\\my-project"。引号要保留。' },
            { label: 'Mac：进入项目文件夹', value: 'cd ~/你的项目文件夹', hint: '例如：cd ~/Documents/my-project。' },
            { label: '在项目文件夹中启动 Codex', value: 'codex', hint: '出现 Codex 输入界面后即可开始提问。' }
          ]
        },
        {
          step: '06',
          order: 6,
          title: '补充：通过 CCSwitch 导入（不是安装方式）',
          summary: '适合已经使用 CCSwitch 管理多套模型或本地开发环境的用户。',
          items: [
            { title: '打开目标密钥', text: '登录本站后进入“API 密钥”，在要使用的密钥所在行点击“导入 CC Switch”。' },
            { title: '允许浏览器唤起 CCSwitch', text: '浏览器会发起 CCSwitch 导入链接；如浏览器询问是否打开应用，选择允许。' },
            { title: '在 CCSwitch 中确认配置', text: '检查导入项后保存并设为当前使用配置，再从 CCSwitch 启动或切换 Codex 工作流。' },
            { title: '回到本站验证', text: '执行一次 Codex 任务后，打开“使用记录”确认本站已记录调用。' }
          ]
        },
        {
          step: '04',
          order: 4,
          title: '方式一：命令行版（CLI）- 启动并验证',
          summary: '一次成功调用后，本站会记录密钥与用量数据。',
          items: [
            { title: '完成一个小任务', text: '例如让 Codex 阅读当前项目结构或解释一个文件，确认客户端能获得正常结果。' },
            { title: '回到本站查看记录', text: '在“使用记录”核对调用时间、模型和 Token；在“API 密钥”确认目标密钥仍为启用状态。' },
            { title: '遇到鉴权或额度问题', text: '优先检查配置文件是否来自当前启用密钥、套餐是否有效、密钥绑定的分组是否有剩余额度；修改后重启 Codex 或 CCSwitch。' }
          ],
          commands: [
            { label: '启动后在 Codex 中输入这一句测试', value: '请读取当前项目结构，并用中文列出每个目录的作用。', hint: '这不是在 PowerShell 中执行的命令，而是在运行 codex 后出现的 Codex 输入界面中粘贴的第一条请求。' }
          ]
        }
      ]
    };
  },
  computed: {
    orderedSections() {
      return this.sections.slice().sort((first, second) => first.order - second.order);
    }
  },
  methods: {
    async copyCommand(value) {
      try {
        await navigator.clipboard.writeText(value);
        this.$message.success('命令已复制，粘贴到终端后按 Enter');
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.$message.success('命令已复制，粘贴到终端后按 Enter');
      }
    }
  }
};
</script>

<style scoped>
.guide-shell {
  width: min(1040px, calc(100% - 32px));
}

.guide-card {
  padding: 42px 38px;
}

.guide-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 30px 0;
}

.guide-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 24px 0 30px;
}

.guide-links a {
  padding: 8px 12px;
  color: #d8cbff;
  border: 1px solid rgba(162, 131, 255, 0.3);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  background: rgba(28, 29, 57, 0.56);
}

.guide-links a:hover {
  color: #ffffff;
  border-color: #a98aff;
  background: rgba(117, 82, 214, 0.28);
}

.guide-methods {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 24px;
}

.guide-method {
  padding: 16px 18px;
  border: 1px solid rgba(162, 131, 255, 0.22);
  border-radius: 8px;
  background: rgba(28, 29, 57, 0.5);
}

.guide-method-active {
  border-color: rgba(183, 149, 255, 0.58);
  background: rgba(89, 58, 165, 0.23);
}

.guide-method span {
  color: #b493ff;
  font-size: 12px;
  font-weight: 700;
}

.guide-method strong {
  display: block;
  margin-top: 8px;
  color: #ffffff;
  font-size: 16px;
}

.guide-method p {
  margin: 7px 0 0;
  color: #b9b3d4;
  font-size: 13px;
  line-height: 1.65;
}

.guide-resources {
  display: grid;
  gap: 8px;
  margin-top: 18px;
}

.guide-resources a {
  color: #d8cbff;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.guide-resources a:hover {
  color: #ffffff;
}

.guide-beginner-tip {
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid rgba(195, 169, 255, 0.42);
  border-left: 3px solid #a982ff;
  border-radius: 6px;
  background: rgba(99, 66, 180, 0.16);
}

.guide-beginner-tip strong {
  color: #f3f0ff;
  font-size: 14px;
}

.guide-beginner-tip p {
  margin: 6px 0 0;
  color: #c5bdda;
  font-size: 14px;
  line-height: 1.7;
}

.guide-overview-item,
.guide-section,
.guide-note {
  border: 1px solid rgba(162, 131, 255, 0.22);
  border-radius: 8px;
  background: rgba(28, 29, 57, 0.72);
}

.guide-overview-item {
  min-height: 148px;
  padding: 18px;
}

.guide-overview-item span,
.guide-section-head > span {
  color: #b493ff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.guide-overview-item strong {
  display: block;
  margin-top: 10px;
  color: #ffffff;
  font-size: 16px;
}

.guide-overview-item p,
.guide-section-head p,
.guide-section li p,
.guide-note p {
  margin: 8px 0 0;
  color: #b9b3d4;
  font-size: 14px;
  line-height: 1.75;
}

.guide-list {
  display: grid;
  gap: 16px;
}

.guide-section {
  padding: 24px;
}

.guide-section-head {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 12px;
}

.guide-section-head h2 {
  margin: 0;
  color: #ffffff;
  font-size: 21px;
}

.guide-section ol {
  display: grid;
  gap: 12px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: guide-step;
}

.guide-section li {
  position: relative;
  min-height: 62px;
  padding: 14px 16px 14px 48px;
  border: 1px solid rgba(167, 143, 240, 0.16);
  border-radius: 6px;
  background: rgba(10, 10, 25, 0.28);
  counter-increment: guide-step;
}

.guide-section li::before {
  position: absolute;
  top: 16px;
  left: 16px;
  color: #9d7ff0;
  content: counter(guide-step, decimal-leading-zero);
  font-size: 12px;
  font-weight: 700;
}

.guide-section li strong,
.guide-note strong {
  color: #f3f0ff;
  font-size: 15px;
}

.guide-command {
  display: grid;
  gap: 8px;
  margin-top: 16px;
}

.guide-commands {
  display: grid;
  gap: 14px;
  margin-top: 16px;
}

.guide-command-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.guide-command-head span {
  color: #b9b3d4;
  font-size: 13px;
}

.guide-command-head .el-button {
  flex: 0 0 auto;
  color: #d8cbff;
  border-color: rgba(162, 131, 255, 0.36);
  background: rgba(28, 29, 57, 0.56);
}

.guide-command code {
  display: block;
  padding: 12px 14px;
  overflow-x: auto;
  white-space: pre-wrap;
  color: #d9cbff;
  font-family: Consolas, "Courier New", monospace;
  background: #0b0b1a;
  border: 1px solid rgba(162, 131, 255, 0.28);
  border-radius: 6px;
}

.guide-command-hint {
  margin: 0;
  color: #9f98bb;
  font-size: 12px;
  line-height: 1.6;
}

.guide-screenshot {
  margin: 18px 0 0;
}

.guide-screenshot img {
  display: block;
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  object-position: top;
  border: 1px solid rgba(162, 131, 255, 0.28);
  border-radius: 6px;
}

.guide-screenshot figcaption {
  margin-top: 8px;
  color: #a9a3c1;
  font-size: 12px;
  line-height: 1.6;
}

.guide-note {
  margin-top: 16px;
  padding: 20px 22px;
}

.guide-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
}

@media (max-width: 820px) {
  .guide-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .guide-card {
    padding: 18px 14px;
  }

  .guide-list {
    gap: 8px;
  }

  .guide-section {
    padding: 14px;
  }

  .guide-section-head {
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 8px;
  }

  .guide-section-head h2 {
    font-size: 18px;
  }

  .guide-section-head p,
  .guide-section li p {
    margin-top: 4px;
    line-height: 1.45;
  }

  .guide-section ol {
    gap: 6px;
    margin-top: 12px;
  }

  .guide-section li {
    min-height: 0;
    padding: 8px 10px 8px 36px;
  }

  .guide-section li::before {
    top: 10px;
    left: 10px;
  }

  .guide-section li strong {
    font-size: 14px;
  }

  .guide-card {
    padding: 18px 14px;
  }

  .guide-overview {
    grid-template-columns: 1fr;
  }

  .guide-methods {
    grid-template-columns: 1fr;
  }

  .guide-actions {
    flex-direction: column;
  }

  .guide-links {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .guide-links a {
    text-align: center;
  }

  .guide-actions .el-button {
    width: 100%;
  }
}
</style>
